import Swal from 'sweetalert2';
import { getApiUrl, API_CONFIG } from '../config/api';
import { clearServiceInfoCache } from './serviceInfoApi';
import { clearCatalogCache } from './catalogCache';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/l10n/es.js';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import { clearTicketExecutionTimer } from './ticketTimerState';
import { getChildHierarchyBadgeLabel, getSupportTicketParentId } from './ticketDisplayRef';
import { notifyDesktopViaServiceWorkerOrFallback } from './webPush';
import { actionSwal, actionSwalDanger } from './supportActionSwal';
import {
  areaRequiresSubAreaStep,
  buildSubAreaGroupedChoicesForSelect,
  mapUsersToChoiceOptions,
} from './assigneeCatalog';

const TERMINAL_TASK_STATUS_IDS = new Set([7, 8]);

/** Hijas abiertas detectadas en las listas ya cargadas en Soporte (respaldo si el GET detalle falla). */
function collectOpenChildTasksFromLists(parentId, ticketsList) {
  if (parentId == null || !Array.isArray(ticketsList) || ticketsList.length === 0) return [];
  const pid = Number(parentId);
  return ticketsList
    .filter((t) => {
      if (getSupportTicketParentId(t) !== pid) return false;
      const sid = t.status?.id ?? t.status_id;
      return !TERMINAL_TASK_STATUS_IDS.has(Number(sid));
    })
    .map((t) => ({
      id: t.id,
      status: {
        id: t.status?.id ?? t.status_id,
        name: t.status?.name ?? t.status_name ?? 'abierta',
      },
    }));
}

/** Hijas que impiden cerrar el padre (estado ≠ Completado ni Cancelado). null = no se pudo leer del servidor. */
async function getPendingSubtasksFromApi(ticketId) {
  try {
    const detailRes = await fetch(getApiUrl('TASK_BY_ID', ticketId), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });
    if (!detailRes.ok) return null;
    const detail = await detailRes.json();
    if (!detail || typeof detail !== 'object' || detail.error) return null;
    if (!Object.prototype.hasOwnProperty.call(detail, 'pending_subtasks')) return null;
    const pending = detail.pending_subtasks;
    return Array.isArray(pending) ? pending : null;
  } catch (e) {
    console.warn('No se pudo consultar subtareas del ticket:', e);
    return null;
  }
}

function swalParentHasOpenChildTasks(pending, mode) {
  const lines = pending.map(
    (c) => `#${c.id} (${escapeHtml(String(c.status?.name || 'abierta'))})`
  );
  const listHtml =
    lines.length === 1
      ? lines[0]
      : `<ul class="swal-parent-pending-ul">${lines.map((l) => `<li>${l}</li>`).join('')}</ul>`;
  const bloqueAccion =
    mode === 'cancel'
      ? {
          impedido: 'No se puede <strong>cancelar</strong> la tarea padre.',
          cierre: 'Cuando todas las hijas estén <strong>completadas</strong> o <strong>canceladas</strong>, podrá <strong>cancelar</strong> la tarea padre.',
        }
      : {
          impedido: 'No se puede <strong>completar</strong> la tarea padre.',
          cierre: 'Cuando todas las hijas estén <strong>completadas</strong> o <strong>canceladas</strong>, podrá <strong>finalizar</strong> la tarea padre.',
        };
  actionSwal({
    title: 'Aún tiene tareas hijas abiertas',
    html: `<div class="swal-parent-pending-body">
      <p>${bloqueAccion.impedido}</p>
      <p>Esta tarea es <strong>padre</strong> y <strong>aún</strong> tiene hijas que no están <strong>completadas</strong> ni <strong>canceladas</strong>.</p>
      <p>${bloqueAccion.cierre}</p>
      <p class="swal-parent-pending-subtitle">Hijas abiertas</p>
      <div class="swal-parent-pending-list-wrap">${listHtml}</div>
    </div>`,
    icon: 'warning',
    width: '34rem',
    confirmButtonText: 'Entendido',
    customClass: {
      popup: 'swal-parent-pending-children',
    },
  });
}

/** Duración del aviso de éxito tras acciones (iniciar, reprogramar, etc.): sin botón OK, cierre automático. */
const ACTION_SUCCESS_TIMER_MS = 2400;
const ACTION_SUCCESS_FALLBACK_MSG = 'Ticket actualizado con éxito';

/**
 * Ejecuta la API dentro del modal de confirmación (spinner en el mismo diálogo).
 * No abre otro Swal de «Actualizando…» para evitar mensajes apilados.
 * @param {() => Promise<T>} asyncFn
 * @returns {Promise<T|false>}
 * @template T
 */
async function runActionInPreConfirm(asyncFn) {
  Swal.showLoading();
  try {
    return await asyncFn();
  } catch (error) {
    Swal.hideLoading();
    Swal.showValidationMessage(error?.message || 'Ocurrió un error');
    return false;
  }
}

/**
 * Muestra éxito que se cierra solo; refresca listas en paralelo (sin bloquear el aviso).
 * @param {string} title
 * @param {string} [text]
 * @param {(() => void|Promise<unknown>)|null|undefined} refreshTickets
 */
function fireActionSuccessWithRefresh(title, text, refreshTickets) {
  if (typeof refreshTickets === 'function') {
    void Promise.resolve(refreshTickets()).catch((err) => {
      console.warn('Error al refrescar tickets tras acción:', err);
    });
  }
  return actionSwal({
    icon: 'success',
    title,
    text: text || ACTION_SUCCESS_FALLBACK_MSG,
    timer: ACTION_SUCCESS_TIMER_MS,
    showConfirmButton: false,
    timerProgressBar: true,
  });
}

async function resolveOpenChildTasksForParent(ticketId, ticketsContextForChildren) {
  let pending = await getPendingSubtasksFromApi(ticketId);
  if (pending === null && ticketsContextForChildren?.length) {
    pending = collectOpenChildTasksFromLists(ticketId, ticketsContextForChildren);
  }
  if (pending === null) pending = [];
  return pending;
}

const USER_PROFILE_TTL_MS = 60 * 1000;
let userProfileMemoryCache = null;
/** Coalesce parallel GET /user_profile antes de que exista caché en memoria. */
let fetchUserProfileInFlight = null;

const SESSION_KEYS_TO_CLEAR_ON_LOGOUT = [
  'auth_token',
  'hs_module_access_cache_v1',
  'hs_module_access_cache_v2',
  'report_dates_session'
];

const LOCAL_KEYS_TO_CLEAR_ON_LOGOUT = [
  'dashboard_activeTab',
  'customer_journeys',
  'improvement_plans',
  'jex_improvement_plan_followups'
];

const LOCAL_PREFIXES_TO_CLEAR_ON_LOGOUT = [
  'dashboard_ticket_exec_timer_v1:',
  'dashboard_ticket_open_timer_v1:'
];

/** Cookie auth_token es httpOnly: no accesible desde JS. Stub para compatibilidad. */
export const getTokenFromCookies = () => null;

/** Limpia cookies no-httpOnly. La cookie auth_token se borra llamando al endpoint /logout. */
const clearAuthData = () => {
  document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  try {
    SESSION_KEYS_TO_CLEAR_ON_LOGOUT.forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {}
  try {
    LOCAL_KEYS_TO_CLEAR_ON_LOGOUT.forEach((key) => localStorage.removeItem(key));
    const keysToRemoveByPrefix = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (LOCAL_PREFIXES_TO_CLEAR_ON_LOGOUT.some((prefix) => key.startsWith(prefix))) {
        keysToRemoveByPrefix.push(key);
      }
    }
    keysToRemoveByPrefix.forEach((key) => localStorage.removeItem(key));
  } catch (_) {}
  // Perfil de usuario solo en memoria para reducir exposición de datos sensibles en storage.
  userProfileMemoryCache = null;
  fetchUserProfileInFlight = null;
  invalidateValidateTokenMemoryCache();
  try {
    clearServiceInfoCache();
  } catch (_) {}
  try {
    clearCatalogCache();
  } catch (_) {}
};

const redirectToLogin = () => {
  window.location.replace('/');
};

// Función centralizada para escapar HTML y prevenir XSS
export const escapeHtml = (text) => {
  if (text === null || text === undefined) return '';
  const str = String(text);
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

/**
 * Texto seguro para títulos/modales: tarea hija → "3.1"; raíz → "#id".
 * Las llamadas API siguen usando ticketId numérico.
 */
function actionTicketRefForSwal(ticketId, uiContext) {
  const ticket = uiContext?.ticket;
  const ticketsInView = uiContext?.ticketsInView;
  const idStr = String(ticketId);
  if (!ticket || getSupportTicketParentId(ticket) == null) {
    return `#${escapeHtml(idStr)}`;
  }
  const h = getChildHierarchyBadgeLabel(ticket, ticketsInView);
  if (h) return escapeHtml(String(h));
  return `#${escapeHtml(idStr)}`;
}

/** Extrae texto de HTML sin ejecutar scripts (previene XSS). Usar en lugar de innerHTML cuando label viene de API/usuario. */
export const safeExtractTextFromHtml = (html) => {
  if (html === null || html === undefined) return '';
  const str = String(html);
  if (!str.trim()) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return (doc.body?.textContent || doc.body?.innerText || '').trim();
  } catch {
    return str.replace(/<[^>]*>/g, '');
  }
};

// Funciones de validación y sanitización de inputs
export const sanitizeInput = (input, options = {}) => {
  if (input === null || input === undefined) return '';
  
  let sanitized = String(input);
  
  // Trim por defecto
  if (options.trim !== false) {
    sanitized = sanitized.trim();
  }
  
  // Remover caracteres de control (excepto saltos de línea y tabs si se permiten)
  if (options.allowNewlines !== true) {
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  } else {
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
    // Mantener saltos de línea y tabs
    sanitized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }
  
  // Limitar longitud si se especifica
  if (options.maxLength && sanitized.length > options.maxLength) {
    sanitized = sanitized.substring(0, options.maxLength);
  }
  
  return sanitized;
};

export const validateUsername = (username) => {
  const sanitized = sanitizeInput(username, { maxLength: 255 });
  
  if (!sanitized || sanitized.length === 0) {
    return { valid: false, error: 'El usuario no puede estar vacío' };
  }
  
  if (sanitized.length < 3) {
    return { valid: false, error: 'El usuario debe tener al menos 3 caracteres' };
  }
  
  if (sanitized.length > 255) {
    return { valid: false, error: 'El usuario no puede exceder 255 caracteres' };
  }
  
  // Permitir letras, números, puntos, guiones, guiones bajos y @ para emails
  if (!/^[a-zA-Z0-9._@-]+$/.test(sanitized)) {
    return { valid: false, error: 'El usuario contiene caracteres no permitidos' };
  }
  
  return { valid: true, value: sanitized };
};

export const validatePassword = (password) => {
  if (!password || password.length === 0) {
    return { valid: false, error: 'La contraseña no puede estar vacía' };
  }
  
  if (password.length < 1) {
    return { valid: false, error: 'La contraseña es requerida' };
  }
  
  // No limitamos demasiado la contraseña para no interferir con políticas del servidor
  // Pero sí removemos caracteres de control peligrosos
  const sanitized = password.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  
  if (sanitized.length > 1000) {
    return { valid: false, error: 'La contraseña es demasiado larga' };
  }
  
  return { valid: true, value: sanitized };
};

export const validateEmail = (email) => {
  const sanitized = sanitizeInput(email, { maxLength: 255 });
  
  if (!sanitized || sanitized.length === 0) {
    return { valid: false, error: 'El email no puede estar vacío' };
  }
  
  // Validación básica de formato de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    return { valid: false, error: 'El formato del email no es válido' };
  }
  
  if (sanitized.length > 255) {
    return { valid: false, error: 'El email no puede exceder 255 caracteres' };
  }
  
  return { valid: true, value: sanitized };
};

export const validateText = (text, options = {}) => {
  const maxLength = options.maxLength || 5000;
  const minLength = options.minLength || 0;
  const required = options.required !== false;
  
  const sanitized = sanitizeInput(text, { 
    maxLength, 
    allowNewlines: options.allowNewlines === true,
    trim: options.trim !== false
  });
  
  if (required && (!sanitized || sanitized.length === 0)) {
    return { valid: false, error: options.errorMessage || 'Este campo es obligatorio' };
  }
  
  if (sanitized.length < minLength) {
    return { valid: false, error: `El texto debe tener al menos ${minLength} caracteres` };
  }
  
  if (sanitized.length > maxLength) {
    return { valid: false, error: `El texto no puede exceder ${maxLength} caracteres` };
  }
  
  return { valid: true, value: sanitized };
};

export const validateComment = (comment) => {
  return validateText(comment, {
    maxLength: 2000,
    minLength: 1,
    required: true,
    allowNewlines: true,
    errorMessage: 'El comentario es obligatorio'
  });
};

export const validateDescription = (description) => {
  return validateText(description, {
    maxLength: 5000,
    minLength: 1,
    required: true,
    allowNewlines: true,
    errorMessage: 'La descripción es obligatoria'
  });
};

export const validateTicketId = (ticketId) => {
  const sanitized = sanitizeInput(ticketId, { maxLength: 100 });
  
  if (!sanitized || sanitized.length === 0) {
    return { valid: false, error: 'El ID no puede estar vacío' };
  }
  
  // Permitir letras, números, guiones, guiones bajos, puntos, barras, espacios y dos puntos
  // (formatos comunes: TKT-123, 123/456, TKT.001, TKT:123, etc.)
  if (!/^[a-zA-Z0-9_\-.\\/ :]+$/.test(sanitized)) {
    return { valid: false, error: 'El ID contiene caracteres no permitidos. Use solo letras, números, guiones (-_), puntos (.), barras (/) o espacios.' };
  }
  
  return { valid: true, value: sanitized };
};

export const validateDate = (dateString) => {
  const sanitized = sanitizeInput(dateString);
  
  if (!sanitized || sanitized.length === 0) {
    return { valid: false, error: 'La fecha no puede estar vacía' };
  }
  
  // Validar formato básico de fecha (ISO o formato común)
  const date = new Date(sanitized);
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'El formato de fecha no es válido' };
  }
  
  return { valid: true, value: sanitized };
};

/** Cache corta para /validate_token: reduce ruido de red (varias vistas + SSE + intervalos). */
let validateTokenMemoryCache = { ts: 0, valid: false };
const VALIDATE_TOKEN_TTL_MS = 4 * 60 * 1000;
/** Una sola petición en vuelo: evita 2× POST si varios componentes validan el mismo instante. */
let validateTokenInFlight = null;

export const invalidateValidateTokenMemoryCache = () => {
  validateTokenMemoryCache = { ts: 0, valid: false };
  validateTokenInFlight = null;
};

// Function to validate token (cookie httpOnly: backend lee auth_token de la cookie)
export const validateToken = async () => {
  const now = Date.now();
  if (validateTokenMemoryCache.valid && now - validateTokenMemoryCache.ts < VALIDATE_TOKEN_TTL_MS) {
    return true;
  }
  if (validateTokenInFlight) {
    return validateTokenInFlight;
  }
  validateTokenInFlight = (async () => {
    try {
      const response = await fetch(getApiUrl('VALIDATE_TOKEN'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (!response.ok) {
        validateTokenMemoryCache = { ts: Date.now(), valid: false };
        return false;
      }
      const data = await response.json();
      const ok = data.valid === true;
      validateTokenMemoryCache = { ts: Date.now(), valid: ok };
      return ok;
    } catch {
      validateTokenMemoryCache = { ts: Date.now(), valid: false };
      return false;
    } finally {
      validateTokenInFlight = null;
    }
  })();
  return validateTokenInFlight;
};

/** Rol visualizador: solo puede ver información, no crear/editar/eliminar. */
export const isReadOnlyUser = (user) => user?.user_type === 'viewer';

/** Super Administrador: ve toda la información sin filtro por empresa. */
export const isSuperAdminUser = (user) => user?.user_type === 'super_admin';

// Function to fetch user profile data (cookie httpOnly enviada con credentials)
const getCachedUserProfile = () => {
  const now = Date.now();
  if (userProfileMemoryCache && now - userProfileMemoryCache.ts < USER_PROFILE_TTL_MS) {
    return userProfileMemoryCache.user;
  }
  return null;
};

const setCachedUserProfile = (user) => {
  const payload = { ts: Date.now(), user };
  userProfileMemoryCache = payload;
};

const patchCachedUserProfile = (patch) => {
  if (!userProfileMemoryCache?.user) return;
  userProfileMemoryCache = {
    ts: Date.now(),
    user: { ...userProfileMemoryCache.user, ...patch },
  };
};

/** Tras cambiar permisos en Team, forzar que el próximo fetchUserProfile vuelva a pedir /user_profile. */
export const invalidateUserProfileMemoryCache = () => {
  userProfileMemoryCache = null;
  fetchUserProfileInFlight = null;
};

/** Perfil en memoria sin red (mismo TTL que fetchUserProfile). Para avisos en tiempo real inmediatos. */
export const getCachedUserProfileSync = () => {
  const now = Date.now();
  if (userProfileMemoryCache && now - userProfileMemoryCache.ts < USER_PROFILE_TTL_MS) {
    return userProfileMemoryCache.user;
  }
  return null;
};

export const fetchUserProfile = async (options = {}) => {
  const { forceFresh = false } = options;
  if (!forceFresh) {
    const cached = getCachedUserProfile();
    if (cached) return cached;
    if (fetchUserProfileInFlight) {
      return fetchUserProfileInFlight;
    }
  }

  const run = (async () => {
    try {
      const response = await fetch(getApiUrl('USER_PROFILE'), {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`UNAUTHORIZED`);
      }

      const data = await response.json();

      if (data.code !== 200 || data.status !== "success" || !data.user) {
        throw new Error("Invalid user profile response structure");
      }

      setCachedUserProfile(data.user);
      return data.user;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      throw error;
    }
  })();

  if (!forceFresh) {
    fetchUserProfileInFlight = run;
    run.finally(() => {
      if (fetchUserProfileInFlight === run) {
        fetchUserProfileInFlight = null;
      }
    });
  }

  return run;
};

/** Persiste preferencia de interfaz Soporte (moderna / antigua) en users.support_ui_version. */
export const saveSupportUiVersionPreference = async (version) => {
  const normalized = version === 'modern' ? 'modern' : 'classic';
  const response = await fetch(getApiUrl('USER_PROFILE'), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ support_ui_version: normalized }),
  });
  if (!response.ok) {
    throw new Error(`Error guardando preferencia de interfaz: ${response.statusText}`);
  }
  const data = await response.json();
  if (data.code !== 200 || data.status !== 'success') {
    throw new Error(data.msg || 'Error guardando preferencia de interfaz');
  }
  patchCachedUserProfile({ support_ui_version: data.support_ui_version || normalized });
  return data.support_ui_version || normalized;
};

/** Tamaño de página alineado con el backend (_paginate_limit_offset) para listas del dashboard de soporte. */
export const SUPPORT_DASHBOARD_TASKS_PAGE_SIZE = 20;

// Function to fetch data from API (cookie httpOnly enviada con credentials)
export const fetchData = async (url) => {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Error fetching data from ${url}: ${response.statusText}`);
  }

  return await response.json();
};

// Function to fetch employee tickets (cookie httpOnly enviada con credentials)
// expandParentId: al enfocar un padre en el dashboard, el API incluye todas las hijas abiertas de ese padre.
export const fetchEmployeeTickets = async (userId, expandParentId = null) => {
  try {
    let url = getApiUrl('TASKS_BY_USER', userId);
    if (
      expandParentId != null &&
      expandParentId !== '' &&
      Number.isFinite(Number(expandParentId))
    ) {
      const sep = url.includes('?') ? '&' : '?';
      url = `${url}${sep}expand_parent=${encodeURIComponent(String(expandParentId))}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Error fetching employee tickets: ${response.statusText}`);
    }

    const tickets = await response.json();
    return tickets;
  } catch (error) {
    console.error("Error fetching employee tickets:", error);
    throw error;
  }
};

// Function to fetch department tickets (cookie httpOnly enviada con credentials)
// expandParentId: ver todas las hijas de un padre visible por área (sin listarlas hasta expandir).
// paginación: limit / offset (query string); la respuesta es { items, total, limit, offset, has_more }.
export const fetchDepartmentTickets = async (
  userId,
  expandParentId = null,
  { limit = SUPPORT_DASHBOARD_TASKS_PAGE_SIZE, offset = 0 } = {}
) => {
  try {
    let url = getApiUrl('TASKS_BY_USER_TASKS', userId);
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    if (
      expandParentId != null &&
      expandParentId !== '' &&
      Number.isFinite(Number(expandParentId))
    ) {
      qs.set('expand_parent', String(expandParentId));
    }
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}${qs.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Error fetching department tickets: ${response.statusText}`);
    }

    const body = await response.json();
    if (Array.isArray(body)) {
      return {
        items: body,
        total: body.length,
        limit: body.length || limit,
        offset: 0,
        has_more: false,
      };
    }
    const items = Array.isArray(body.items) ? body.items : [];
    return {
      items,
      total: Number(body.total) || 0,
      limit: Number(body.limit) || limit,
      offset: Number(body.offset) || offset,
      has_more: Boolean(body.has_more),
      status_counts: body.status_counts || null,
    };
  } catch (error) {
    console.error('Error fetching department tickets:', error);
    throw error;
  }
};

/** Todas las tareas abiertas de las áreas (sin completadas ni canceladas). Paginación: limit / offset. */
export const fetchOpenAllAreasTickets = async ({
  limit = SUPPORT_DASHBOARD_TASKS_PAGE_SIZE,
  offset = 0,
} = {}) => {
  try {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    const response = await fetch(
      `${getApiUrl('TASKS_OPEN_ALL_AREAS')}?${qs.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    if (!response.ok) {
      throw new Error(`Error al cargar todas las tareas: ${response.statusText}`);
    }
    const body = await response.json();
    if (Array.isArray(body)) {
      return {
        items: body,
        total: body.length,
        limit: body.length || limit,
        offset: 0,
        has_more: false,
      };
    }
    const items = Array.isArray(body.items) ? body.items : [];
    return {
      items,
      total: Number(body.total) || 0,
      limit: Number(body.limit) || limit,
      offset: Number(body.offset) || offset,
      has_more: Boolean(body.has_more),
      status_counts: body.status_counts || null,
    };
  } catch (error) {
    console.error('Error fetching open all-areas tickets:', error);
    throw error;
  }
};


// Function to fetch cities (cookie httpOnly enviada con credentials)
export const fetchCities = async () => {
  try {
    const response = await fetch(getApiUrl('CITY'), {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Error al obtener ciudades: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.result || [];
  } catch (error) {
    console.error('Error fetching cities:', error);
    throw error;
  }
};

// Function to fetch departments by city (cookie httpOnly enviada con credentials)
export const fetchDepartmentsByCity = async (cityId) => {
  try {
    const response = await fetch(getApiUrl('AREAS_BY_CITY', cityId), {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Error al obtener áreas: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.result || [];
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Function to fetch users by city and department (cookie httpOnly enviada con credentials)
export const fetchUsersByCityDepartment = async (cityId, departmentId) => {
  try {
    const url = getApiUrl('USERS_BY_CITY_AREA', cityId, departmentId);
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error response:', errorData);
      throw new Error(`Error al obtener usuarios: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const users = data.result || data.users || data || [];
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Function to fetch subareas by city and area (cookie httpOnly enviada con credentials)
export const fetchSubAreasByCityAndArea = async (cityId, areaId) => {
  try {
    const response = await fetch(getApiUrl('SUB_AREAS_BY_CITY_AREA', cityId, areaId), {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Error al obtener Torres: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.result || [];
  } catch (error) {
    console.error('Error fetching subareas:', error);
    throw error;
  }
};

// Function to fetch users by city, area and subarea (cookie httpOnly enviada con credentials)
export const fetchUsersByCityAreaSubarea = async (cityId, areaId, subAreaId) => {
  try {
    const url = getApiUrl('USERS_BY_CITY_AREA_SUBAREA', cityId, areaId, subAreaId);
    console.log('Fetching users from URL:', url);
    
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error response:', errorData);
      throw new Error(`Error al obtener usuarios: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const users = data.result || data.users || data || [];
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Function to perform API call for task updates (cookie httpOnly enviada con credentials)
const updateTask = async (ticketId, payload) => {
  try {
    const response = await fetch(getApiUrl('TASK_BY_ID', ticketId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error updating task: ${response.statusText}`);
    }

    const result = await response.json(); 
    if (result.code !== 200) {
      throw new Error(result.msg || 'Unexpected server response');
    }
    return result; 
  } catch (error) {
    console.error(`Error updating task ${ticketId}:`, error);
    throw error;
  }
};


export const handleStartTicket = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwal({
    title: `Iniciar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <textarea id="comment" class="form-control swal-support-field mt-2" placeholder="Añadir comentario (obligatorio)"></textarea>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Sí, iniciar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const commentInput = document.getElementById('comment').value;
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      return runActionInPreConfirm(async () => {
        const response = await updateTask(ticketId, {
          action: 'Iniciar',
          executed_at: new Date().toISOString(),
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: commentValidation.value,
        });
        // Reinicia el cronómetro local: el tiempo de ejecución empieza en el Iniciar, no desde la creación.
        clearTicketExecutionTimer(ticketId);
        return response;
      });
    },
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea iniciada', result.value?.msg, refreshTickets);
    }
  });
};


export const handleReprogramTicket = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  const motivoOptions = [
    'Falta de materiales',
    'Falta de herramientas',
    'Falta de personal disponible',
    'Falta de acceso al sitio',
    'Permiso o autorización pendiente',
    'Condiciones climáticas adversas',
    'Esperando aprobación del cliente',
    'Equipo averiado o en mantenimiento',
    'Falta de información técnica',
    'Riesgo de seguridad identificado',
    'Intervención de otro equipo o área',
    'Corte de energía o red',
    'Tarea duplicada o cancelada',
    'Error en la orden de trabajo',
    'Horario no permitido para la ejecución'
  ];

  actionSwal({
    title: `Reprogramar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <div class="mb-3">
          <label for="execution-date" class="form-label">Fecha y hora de ejecución</label>
          <input type="text" id="execution-date" class="form-control swal-support-field" placeholder="dd/mm/aaaa hh:mm" required>
        </div>
        <div class="mb-3">
          <label for="motivoSelect" class="form-label">Motivo de reprogramación</label>
          <select id="motivoSelect" class="form-select swal-support-field" required>
            <option value="">Seleccionar motivo</option>
          </select>
        </div>
        <div class="mb-2">
          <label for="comment" class="form-label">Comentario adicional</label>
          <textarea id="comment" class="form-control swal-support-field" placeholder="Añadir comentario (obligatorio)"></textarea>
        </div>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Sí, reprogramar',
    cancelButtonText: 'Cancelar',
    didOpen: () => {
      // Inicializar flatpickr
      if (typeof flatpickr !== 'undefined') {
        flatpickr('#execution-date', {
          enableTime: true,
          dateFormat: 'Y-m-d H:i',
          altInput: true,
          altFormat: 'd/m/Y H:i',
          minDate: 'today',
          time_24hr: true,
          locale: 'es',
          disableMobile: true,
        });
      }

      // Inicializar Choices.js para el select de motivos
      const motivoSelect = document.getElementById('motivoSelect');
      const motivoChoices = new Choices(motivoSelect, {
        searchEnabled: true,
        placeholderValue: 'Seleccionar motivo',
        searchPlaceholderValue: 'Buscar motivo',
        itemSelectText: '',
        shouldSort: false,
        noResultsText: 'No se encontraron motivos',
        noChoicesText: 'No hay motivos disponibles',
      });

      // Configurar las opciones del select
      if (motivoOptions.length === 0) {
        motivoChoices.setChoices([{ value: '', label: 'No hay motivos disponibles', disabled: true }], 'value', 'label', true);
        motivoChoices.disable();
      } else {
        motivoChoices.setChoices(
          motivoOptions.map(motivo => ({ 
            value: motivo, 
            label: motivo 
          })), 
          'value', 
          'label', 
          true
        );
        motivoChoices.enable();
      }
    },
    willClose: () => {
      // Limpiar instancias de Choices.js al cerrar el modal
      const motivoSelect = document.getElementById('motivoSelect');
      if (motivoSelect && motivoSelect._choices) {
        motivoSelect._choices.destroy();
      }

      const executionDateInput = document.getElementById('execution-date');
      if (executionDateInput && executionDateInput._flatpickr) {
        executionDateInput._flatpickr.destroy();
      }
    },
    preConfirm: async () => {
      const executionDate = document.getElementById('execution-date').value;
      const motivo = document.getElementById('motivoSelect').value;
      const commentInput = document.getElementById('comment').value;

      if (!executionDate) {
        Swal.showValidationMessage('Por favor selecciona una fecha de ejecución');
        return false;
      }
      
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      if (!motivo) {
        Swal.showValidationMessage('Por favor selecciona un motivo');
        return false;
      }
      return runActionInPreConfirm(async () =>
        updateTask(ticketId, {
          action: 'Reprogramar',
          executed_at: executionDate,
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: `${motivo}: ${commentValidation.value}`,
        })
      );
    }
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea reprogramada', result.value?.msg, refreshTickets);
    }
  });
};


export const handleReassignTicket = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwal({
    title: `Reasignar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <p>¿Deseas reasignar la tarea ${ref}?</p>
        <div style="margin-bottom: 12px;">
          <select id="citySelect" class="form-select swal-support-field">
            <option value="">Seleccionar ciudad</option>
          </select>
        </div>
        <div style="margin-bottom: 12px;">
          <select id="areaSelect" class="form-select swal-support-field" disabled>
            <option value="">Seleccionar área</option>
          </select>
        </div>
        <div id="subAreaContainer" style="margin-bottom: 12px; display: none;">
          <select id="subAreaSelect" class="form-select swal-support-field" disabled>
            <option value="">Seleccionar Torre</option>
          </select>
        </div>
        <div style="margin-bottom: 12px;">
          <select id="userSelect" class="form-select swal-support-field" disabled>
            <option value="">Seleccionar usuario</option>
          </select>
        </div>
        <div style="margin-bottom: 12px;">
          <input type="text" id="executionDate" class="form-control swal-support-field" placeholder="Selecciona fecha y hora">
        </div>
        <div style="margin-bottom: 12px;">
          <textarea id="comment" class="form-control swal-support-field" placeholder="Añadir comentario (obligatorio)"></textarea>
        </div>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Reasignar',
    cancelButtonText: 'Cancelar',
    didOpen: async () => {
      try {
        const citySelect = document.getElementById('citySelect');
        const areaSelect = document.getElementById('areaSelect');
        const subAreaSelect = document.getElementById('subAreaSelect');
        const subAreaContainer = document.getElementById('subAreaContainer');
        const userSelect = document.getElementById('userSelect');

        // Inicializar Choices para todos los selects
        const cityChoices = new Choices(citySelect, {
          searchEnabled: true,
          placeholderValue: 'Seleccionar ciudad',
          searchPlaceholderValue: 'Buscar ciudad',
          itemSelectText: '',
          shouldSort: false,
          noResultsText: 'No se encontraron ciudades',
          noChoicesText: 'No hay ciudades disponibles',
        });

        const areaChoices = new Choices(areaSelect, {
          searchEnabled: true,
          placeholderValue: 'Seleccionar área',
          searchPlaceholderValue: 'Buscar área',
          itemSelectText: '',
          shouldSort: false,
          noResultsText: 'No se encontraron áreas',
          noChoicesText: 'No hay áreas disponibles',
        });

        const subAreaChoices = new Choices(subAreaSelect, {
          searchEnabled: true,
          placeholderValue: 'Seleccionar Torre',
          searchPlaceholderValue: 'Buscar Torre',
          itemSelectText: '',
          shouldSort: false,
          noResultsText: 'No se encontraron Torres',
          noChoicesText: 'No hay Torres disponibles',
        });

        const userChoices = new Choices(userSelect, {
          searchEnabled: true,
          placeholderValue: 'Seleccionar usuario',
          searchPlaceholderValue: 'Buscar usuario',
          itemSelectText: '',
          shouldSort: false,
          noResultsText: 'No se encontraron usuarios',
          noChoicesText: 'No hay usuarios disponibles',
        });

        // Deshabilitar selects inicialmente
        areaChoices.disable();
        subAreaChoices.disable();
        userChoices.disable();
        
        areaChoices.setChoices([{ value: '', label: 'Seleccionar área', disabled: true }], 'value', 'label', true);
        subAreaChoices.setChoices([{ value: '', label: 'Seleccionar Torre', disabled: true }], 'value', 'label', true);
        userChoices.setChoices([{ value: '', label: 'Seleccionar usuario', disabled: true }], 'value', 'label', true);

        // Cargar ciudades
        const cities = await fetchCities();
        if (cities.length === 0) {
          cityChoices.setChoices([{ value: '', label: 'No hay ciudades disponibles', disabled: true }], 'value', 'label', true);
          cityChoices.disable();
        } else {
          cityChoices.setChoices(cities.map(city => ({ value: city.id, label: city.name })), 'value', 'label', true);
          cityChoices.enable();
        }

        // Variable para almacenar las áreas cargadas
        let loadedAreas = [];

        const shouldShowSubArea = (areaId) => areaRequiresSubAreaStep(areaId, loadedAreas);

        // Función para cargar usuarios directamente (sin Torre)
        const loadUsersDirectly = async (cityId, areaId) => {
          try {
            const users = await fetchUsersByCityDepartment(cityId, areaId);
            
            if (users.length === 0) {
              userChoices.setChoices([{ value: '', label: 'No hay usuarios disponibles', disabled: true }], 'value', 'label', true);
              userChoices.disable();
            } else {
              userChoices.setChoices(mapUsersToChoiceOptions(users), 'value', 'label', true);
              userChoices.enable();
            }
          } catch (error) {
            console.error('Error loading users:', error);
            userChoices.setChoices([{ value: '', label: 'Error al cargar usuarios', disabled: true }], 'value', 'label', true);
            userChoices.disable();
          }
        };

        // Función para cargar usuarios por Torre
        const loadUsersBySubArea = async (cityId, areaId, subAreaId) => {
          try {
            const users = await fetchUsersByCityAreaSubarea(cityId, areaId, subAreaId);
            
            if (users.length === 0) {
              userChoices.setChoices([{ value: '', label: 'No hay usuarios disponibles', disabled: true }], 'value', 'label', true);
              userChoices.disable();
            } else {
              userChoices.setChoices(mapUsersToChoiceOptions(users), 'value', 'label', true);
              userChoices.enable();
            }
          } catch (error) {
            console.error('Error loading users:', error);
            userChoices.setChoices([{ value: '', label: 'Error al cargar usuarios', disabled: true }], 'value', 'label', true);
            userChoices.disable();
          }
        };

        // Evento cuando cambia la ciudad
        citySelect.addEventListener('change', async () => {
          const cityId = citySelect.value;
          
          // Limpiar selects dependientes
          areaChoices.clearStore();
          subAreaChoices.clearStore();
          userChoices.clearStore();
          
          areaChoices.setChoices([{ value: '', label: 'Seleccionar área', disabled: true }], 'value', 'label', true);
          subAreaChoices.setChoices([{ value: '', label: 'Seleccionar Torre', disabled: true }], 'value', 'label', true);
          userChoices.setChoices([{ value: '', label: 'Seleccionar usuario', disabled: true }], 'value', 'label', true);
          
          areaChoices.disable();
          subAreaChoices.disable();
          userChoices.disable();

          if (subAreaContainer) subAreaContainer.style.display = 'none';

          if (cityId) {
            try {
              loadedAreas = await fetchDepartmentsByCity(cityId);
              
              if (loadedAreas.length === 0) {
                areaChoices.setChoices([{ value: '', label: 'No hay áreas disponibles', disabled: true }], 'value', 'label', true);
                areaChoices.disable();
              } else {
                areaChoices.setChoices(loadedAreas.map(area => ({ value: area.id, label: area.name })), 'value', 'label', true);
                areaChoices.enable();
              }
            } catch (error) {
              console.error('Error loading areas:', error);
              areaChoices.setChoices([{ value: '', label: 'Error al cargar áreas', disabled: true }], 'value', 'label', true);
              areaChoices.disable();
            }
          }
        }, { once: false });

        // Evento cuando cambia el área
        areaSelect.addEventListener('change', async () => {
          const areaId = areaSelect.value;
          const cityId = citySelect.value;
          
          // Limpiar selects dependientes
          subAreaChoices.clearStore();
          userChoices.clearStore();
          
          subAreaChoices.setChoices([{ value: '', label: 'Seleccionar Torre', disabled: true }], 'value', 'label', true);
          userChoices.setChoices([{ value: '', label: 'Seleccionar usuario', disabled: true }], 'value', 'label', true);
          
          subAreaChoices.disable();
          userChoices.disable();

          if (areaId && cityId) {
            
            // Verificar si necesita Torre
            if (shouldShowSubArea(areaId)) {
              console.log('Mostrando Torre para área:', areaId);
              // Mostrar Torre y cargar Torres
              subAreaContainer.style.display = 'block';
              try {
                const subAreas = await fetchSubAreasByCityAndArea(cityId, areaId);
                
                if (subAreas.length === 0) {
                  subAreaChoices.setChoices([{ value: '', label: 'No hay Torres disponibles', disabled: true }], 'value', 'label', true);
                  subAreaChoices.disable();
                } else {
                  const grouped = buildSubAreaGroupedChoicesForSelect(subAreas);
                  if (grouped.length > 0) {
                    subAreaChoices.setChoiceByValue('');
                    subAreaChoices.setChoices(grouped, 'value', 'label', false);
                  } else {
                    subAreaChoices.setChoices(
                      subAreas.map(subArea => ({ value: subArea.id, label: subArea.name })),
                      'value',
                      'label',
                      true
                    );
                  }
                  subAreaChoices.enable();
                }
              } catch (error) {
                console.error('Error loading subareas:', error);
                subAreaChoices.setChoices([{ value: '', label: 'Error al cargar Torres', disabled: true }], 'value', 'label', true);
                subAreaChoices.disable();
              }
            } else {
              // Ocultar Torre y cargar usuarios directamente
              subAreaContainer.style.display = 'none';
              await loadUsersDirectly(cityId, areaId);
            }
          }
        }, { once: false });

        // Evento cuando cambia la Torre (solo para áreas que necesitan Torre)
        subAreaSelect.addEventListener('change', async () => {
          const subAreaId = subAreaSelect.value;
          const areaId = areaSelect.value;
          const cityId = citySelect.value;
          
          // Limpiar select de usuarios
          userChoices.clearStore();
          userChoices.setChoices([{ value: '', label: 'Seleccionar usuario', disabled: true }], 'value', 'label', true);
          userChoices.disable();

          // Solo procesar si necesita Torre y hay valores seleccionados
          if (shouldShowSubArea(areaId) && subAreaId && areaId && cityId) {
            console.log('Cargando usuarios por Torre:', subAreaId);
            await loadUsersBySubArea(cityId, areaId, subAreaId);
          }
        }, { once: false });

        // Inicializar flatpickr para fecha de ejecución
        const executionDateInput = document.getElementById('executionDate');
        if (typeof flatpickr !== 'undefined') {
          flatpickr(executionDateInput, {
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            altInput: true,
            altFormat: 'd/m/Y H:i',
            minDate: 'today',
            time_24hr: true,
            locale: 'es',
            disableMobile: true,
            defaultDate: new Date(),
          });
        } else {
          console.error('Flatpickr is not loaded. Please include flatpickr.js and flatpickr.css.');
          executionDateInput.type = 'datetime-local';
          executionDateInput.value = new Date().toISOString().slice(0, 16);
        }
      } catch (error) {
        console.error('Error loading cities:', error);
        Swal.showValidationMessage('Error al cargar ciudades: ' + error.message);
      }
    },
    willClose: () => {
      // Limpiar instancias de Choices.js al cerrar el modal
      const citySelect = document.getElementById('citySelect');
      const areaSelect = document.getElementById('areaSelect');
      const subAreaSelect = document.getElementById('subAreaSelect');
      const userSelect = document.getElementById('userSelect');
      
      if (citySelect && citySelect._choices) citySelect._choices.destroy();
      if (areaSelect && areaSelect._choices) areaSelect._choices.destroy();
      if (subAreaSelect && subAreaSelect._choices) subAreaSelect._choices.destroy();
      if (userSelect && userSelect._choices) userSelect._choices.destroy();

      const executionDateInput = document.getElementById('executionDate');
      if (executionDateInput && executionDateInput._flatpickr) executionDateInput._flatpickr.destroy();
    },
    preConfirm: async () => {
      const selectedUser = document.getElementById('userSelect').value;
      const executionDate = document.getElementById('executionDate').value;
      const commentInput = document.getElementById('comment').value;

      if (!selectedUser) {
        Swal.showValidationMessage('Por favor selecciona un usuario');
        return false;
      }
      
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      if (!executionDate) {
        Swal.showValidationMessage('Por favor selecciona una fecha y hora');
        return false;
      }
      return runActionInPreConfirm(async () =>
        updateTask(ticketId, {
          action: 'Reasignar',
          executed_at: executionDate,
          assigned_by_id: userDbId,
          assigned_to_id: parseInt(selectedUser, 10),
          user_update: userDbId,
          description: commentValidation.value,
        })
      );
    }
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea reasignada', result.value?.msg, refreshTickets);
    }
  });
};


export const handleCancelTicket = async (ticketId, userDbId, refreshTickets, ticketsContextForChildren = null, uiContext = null) => {
  const pending = await resolveOpenChildTasksForParent(ticketId, ticketsContextForChildren);
  if (pending.length > 0) {
    swalParentHasOpenChildTasks(pending, 'cancel');
    return;
  }

  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwalDanger({
    title: `Cancelar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <p>¿Esta acción no se puede deshacer.</p>
        <textarea id="comment" class="form-control swal-support-field mt-2" placeholder="Añadir comentario (obligatorio)"></textarea>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Sí, cancelar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const commentInput = document.getElementById('comment').value;
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      return runActionInPreConfirm(async () =>
        updateTask(ticketId, {
          action: 'Cancelar',
          executed_at: new Date().toISOString(),
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: commentValidation.value,
        })
      );
    }
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea cancelada', result.value?.msg, refreshTickets);
    }
  });
};


// Modificar la función handleFinishTicket existente
export const handleFinishTicket = async (ticket, userDbId, refreshTickets, ticketsContextForChildren = null, uiContext = null) => {
  const ticketId = ticket.id || ticket;
  
  // Validar que el ticket no esté ya completado o cancelado
  const currentStatus = ticket.status?.id || ticket.status_id;
  const statusName = ticket.status?.name || ticket.status_name;
  
  if (currentStatus === 8) {
    actionSwal({
      title: 'Tarea ya completada',
      text: 'Esta tarea ya está completada. No se puede completar nuevamente.',
      icon: 'info',
      confirmButtonText: 'Aceptar'
    });
    return;
  }
  
  if (currentStatus === 7) {
    actionSwal({
      title: 'Tarea cancelada',
      text: 'Esta tarea está cancelada y no se puede completar.',
      icon: 'warning',
      confirmButtonText: 'Aceptar'
    });
    return;
  }
  
  // Validar que el ticket esté en un estado que permita completar (solo desde Ejecución - estado 5)
  if (currentStatus !== 5) {
    actionSwal({
      title: 'Acción no permitida',
      text: `No se puede completar una tarea en estado "${statusName || 'actual'}". Solo se puede completar desde el estado "Ejecución".`,
      icon: 'warning',
      confirmButtonText: 'Aceptar'
    });
    return;
  }

  const pendingSubs = await resolveOpenChildTasksForParent(ticketId, ticketsContextForChildren);
  if (pendingSubs.length > 0) {
    swalParentHasOpenChildTasks(pendingSubs, 'finish');
    return;
  }

  const ref = actionTicketRefForSwal(ticketId, {
    ticket: typeof ticket === 'object' && ticket !== null ? ticket : null,
    ticketsInView: uiContext?.ticketsInView,
  });
  actionSwal({
    title: `Finalizar Tarea ${ref}`,
    html: `
      <div style="font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; line-height: 1.5; color: #2d3748;">
        
        <div style="margin-bottom: 20px;">
          <label style="font-weight: 600; font-size: 14px; display: block; margin-bottom: 9px;">
            Tipo de Soporte <span style="color: #e53e3e;">*</span>
          </label>
          <div style="display: flex; gap: 10px;">
            <label style="flex: 1; position: relative; cursor: pointer;">
              <input type="radio" name="support-type" value="1" class="sr-only" >
              <div class="radio-btn">En sitio</div>
            </label>
            <label style="flex: 1; position: relative; cursor: pointer;">
              <input type="radio" name="support-type" value="2" class="sr-only">
              <div class="radio-btn">Remoto</div>
            </label>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <label style="font-weight: 600; font-size: 14px; display: block; margin-bottom: 9px;">
            Cliente se encuentra <span style="color: #e53e3e;">*</span>
          </label>
          <div style="display: flex; gap: 10px;">
            <label style="flex: 1; position: relative; cursor: pointer;">
              <input type="radio" name="client-location" value="1" class="sr-only" >
              <div class="radio-btn">En sitio</div>
            </label>
            <label style="flex: 1; position: relative; cursor: pointer;">
              <input type="radio" name="client-location" value="2" class="sr-only">
              <div class="radio-btn">Remoto</div>
            </label>
          </div>
        </div>

        <!-- Comentario del técnico -->
        <div style="margin-bottom: 16px;">
          <label style="font-weight: 600; font-size: 14px; display: block; margin-bottom: 9px;">
            Comentario / Solución aplicada <span style="color: #e53e3e;">*</span>
          </label>
          <div style="position: relative;">
            <textarea
              id="comment"
              class="swal-support-field"
              placeholder="Describe detalladamente los pasos realizados, solución aplicada y resultado final..."
              maxlength="1500"
              style="width: 100%; height: 120px; resize: vertical;"></textarea>
            <div id="char-counter" style="
              position: absolute;
              bottom: 10px;
              right: 14px;
              font-size: 11.5px;
              color: #94a3b8;
              background: white;
              padding: 2px 6px;
              border-radius: 4px;
              pointer-events: none;
            ">0 / 1500</div>
          </div>
        </div>

        <div style="text-align: right; font-size: 12px; color: #718096; margin-top: -8px;">
          * Campos obligatorios
        </div>
      </div>
    `,
    didOpen: () => {
      // Estilos personalizados para los radio buttons
      const style = document.createElement('style');
      style.textContent = `
        .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
        .radio-btn {
          padding: 12px 20px;
          text-align: center;
          font-weight: 500;
          font-size: 14px;
          color: #4a5568;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 14px;
          transition: all 0.25s ease;
        }
        input[type="radio"]:checked + .radio-btn {
          background: #dbeafe;
          border-color: #3b82f6;
          color: #1e40af;
          font-weight: 600;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
        }
        .radio-btn:hover {
          border-color: #93c5fd;
          background: #ebf8ff;
          transform: translateY(-1px);
        }
        #comment:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
        }
        #char-counter {
          transition: color 0.3s;
        }
      `;
      document.head.appendChild(style);

      // Contador de caracteres en tiempo real
      const textarea = document.getElementById('comment');
      const counter = document.getElementById('char-counter');
      const updateCounter = () => {
        const length = textarea.value.length;
        counter.textContent = `${length} / 1500`;
        if (length > 1300) counter.style.color = '#e53e3e';
        else if (length > 1000) counter.style.color = '#dd6b20';
        else counter.style.color = '#94a3b8';
      };
      textarea.addEventListener('input', updateCounter);
      updateCounter();
    },
    width: '620px',
    padding: '28px',
    showCancelButton: true,
    confirmButtonText: 'Finalizar Tarea',
    cancelButtonText: 'Cancelar',
    focusConfirm: false,
    allowOutsideClick: false,
    preConfirm: async () => {
      const commentInput = document.getElementById('comment').value;
      const supportType = document.querySelector('input[name="support-type"]:checked')?.value;
      const clientLocation = document.querySelector('input[name="client-location"]:checked')?.value;

      const errors = [];
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        errors.push(commentValidation.error);
      }
      if (!supportType) errors.push('Selecciona el tipo de soporte');
      if (!clientLocation) errors.push('Selecciona dónde se encuentra el cliente');

      if (errors.length > 0) {
        Swal.showValidationMessage(errors.join('<br>'));
        return false;
      }

      const tipoSoporte = supportType === '1' ? 'En sitio' : 'Remoto';
      const clienteUbicacion = clientLocation === '1' ? 'En sitio' : 'Remoto';

      const descriptionFinal = `${commentValidation.value}

Tipo de Soporte: ${tipoSoporte}
Cliente se encuentra: ${clienteUbicacion}`.trim();

      return runActionInPreConfirm(async () => {
        const response = await updateTask(ticketId, {
          action: 'Completar',
          executed_at: new Date().toISOString(),
          assigned_by_id: userDbId,
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: descriptionFinal,
          support_location_id: parseInt(supportType, 10),
          client_location_id: parseInt(clientLocation, 10),
        });
        clearTicketExecutionTimer(ticketId);
        return response;
      });
    }
  }).then(async (result) => {
    if (!result.isConfirmed) return;

    const successMessage = result.value?.msg || ACTION_SUCCESS_FALLBACK_MSG;
    if (typeof refreshTickets === 'function') {
      void Promise.resolve(refreshTickets()).catch((err) => {
        console.warn('Error al refrescar tickets tras finalizar:', err);
      });
    }

    await actionSwal({
      title: '¡Tarea finalizada!',
      text: successMessage,
      icon: 'success',
      timer: ACTION_SUCCESS_TIMER_MS,
      showConfirmButton: false,
      timerProgressBar: true,
    });

    /** Hoja de servicio / PDF solo en raíces (padre o tarea independiente), no en subtareas. */
    const ticketObj = typeof ticket === 'object' && ticket !== null ? ticket : null;
    const isChildTask = getSupportTicketParentId(ticketObj) != null;
    if (!isChildTask && typeof uiContext?.offerHojaServicioAfterFinalize === 'function') {
      const ask = await actionSwal({
        title: 'Hoja de servicio',
        html:
          '<p style="margin:0;color:#334155;font-size:15px;line-height:1.5;">¿Desea generar o enviar la <strong>hoja de servicio</strong> (PDF) para esta tarea ahora?</p>',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, abrir',
        cancelButtonText: 'Ahora no',
      });
      if (ask.isConfirmed) {
        try {
          await uiContext.offerHojaServicioAfterFinalize(ticketId);
        } catch (e) {
          console.warn('offerHojaServicioAfterFinalize:', e);
        }
      }
    }
  });
};


export const handlePauseTicket = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwal({
    title: `Pausar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <p>¿Deseas pausar la tarea ${ref}?</p>
        <textarea id="comment" class="form-control swal-support-field mt-2" placeholder="Añadir comentario (obligatorio)"></textarea>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Sí, pausar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const commentInput = document.getElementById('comment').value;
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      return runActionInPreConfirm(async () =>
        updateTask(ticketId, {
          action: 'Pausar',
          executed_at: new Date().toISOString(),
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: commentValidation.value,
        })
      );
    },
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea pausada', result.value?.msg, refreshTickets);
    }
  });
};

export const handleResumeTicket = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwal({
    title: `Reanudar Tarea ${ref}`,
    html: `
      <div style="width: 100%;">
        <textarea id="comment" class="form-control swal-support-field mt-2" placeholder="Añadir comentario (obligatorio)"></textarea>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Sí, reanudar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const commentInput = document.getElementById('comment').value;
      const commentValidation = validateComment(commentInput);
      if (!commentValidation.valid) {
        Swal.showValidationMessage(commentValidation.error);
        return false;
      }
      return runActionInPreConfirm(async () => {
        const response = await updateTask(ticketId, {
          action: 'Reanudar',
          executed_at: new Date().toISOString(),
          assigned_to_id: userDbId,
          user_update: userDbId,
          description: commentValidation.value,
        });
        clearTicketExecutionTimer(ticketId);
        return response;
      });
    },
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Tarea reanudada', result.value?.msg, refreshTickets);
    }
  });
};


export const handleAddFollowup = async (ticketId, userDbId, statusId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwal({
    title: `Agregar Seguimiento ${ref}`,
    html: `
      <div style="width: 100%;">
        <textarea id="followupDescription" class="form-control swal-support-field mt-2" placeholder="Escribe el detalle del seguimiento (obligatorio)"></textarea>
      </div>
    `,
    icon: '',
    width: '600px',
    showCancelButton: true,
    confirmButtonText: 'Agregar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const descriptionInput = document.getElementById('followupDescription').value;
      const descriptionValidation = validateDescription(descriptionInput);
      if (!descriptionValidation.valid) {
        Swal.showValidationMessage(descriptionValidation.error);
        return false;
      }
      return runActionInPreConfirm(async () =>
        submitTaskFollowup(ticketId, userDbId, statusId, descriptionValidation.value, null, {
          showSuccessToast: false,
        })
      );
    },
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh(
        'Seguimiento agregado',
        result.value?.data?.msg || result.value?.msg,
        refreshTickets
      );
    }
  });
};

/** Registra un seguimiento sin diálogo (composer inline en bandeja). */
export async function submitTaskFollowup(
  ticketId,
  userDbId,
  statusId,
  description,
  refreshTickets,
  options = {}
) {
  const { showSuccessToast = true } = options;
  const descriptionValidation = validateDescription(description);
  if (!descriptionValidation.valid) {
    throw new Error(descriptionValidation.error);
  }

  const payload = {
    task_id: ticketId,
    description: descriptionValidation.value,
    user_id: userDbId,
    status_id: statusId,
    user_create: userDbId,
  };

  const response = await fetch(getApiUrl('TASK_FOLLOWUPS'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const textResponse = await response.text();
    console.error('❌ Respuesta no es JSON:', textResponse);
    throw new Error(`Error del servidor (${response.status}): ${response.statusText}`);
  }

  const data = await response.json();

  if (!response.ok || data.code !== 201) {
    throw new Error(data.error || data.msg || 'Error al agregar el seguimiento');
  }

  if (showSuccessToast) {
    fireActionSuccessWithRefresh('Seguimiento agregado', data.data?.msg, refreshTickets);
  } else if (typeof refreshTickets === 'function') {
    void Promise.resolve(refreshTickets()).catch((err) => {
      console.warn('Error al refrescar tickets tras seguimiento:', err);
    });
  }

  return data;
}



export const handleAddFile = async (ticketId, userDbId, refreshTickets, uiContext = null) => {
  let dropzoneInstance = null;
  const ref = actionTicketRefForSwal(ticketId, uiContext);

  const result = await actionSwal({
    title: `Agregar archivos a la Tarea ${ref}`,
    html: `
      <div class="container-fluid p-3">
        <div id="file-dropzone" class="dropzone swal-support-dropzone p-2" style="min-height: 100px; max-height: 200px;">
          <div class="dz-message needsclick text-center" style="padding: 8px;">
            <i class="bi bi-cloud-arrow-up fs-4 text-muted mb-1"></i>
            <div class="small">Suelta archivos aquí o haz clic</div>
            <div class="text-muted" style="font-size: 0.75rem;">Máximo 10MB por archivo - Hasta 10 archivos</div>
          </div>
        </div>
        <div id="upload-progress" class="mt-3" style="display: none;">
          <div class="progress" style="height: 8px; border-radius: 999px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="width: 0%; border-radius: 999px;"></div>
          </div>
          <div class="small text-center mt-1">Subiendo archivos...</div>
        </div>
      </div>
    `,
    icon: '',
    width: '700px',
    showCancelButton: true,
    confirmButtonText: 'Subir Archivos',
    cancelButtonText: 'Cancelar',
    allowOutsideClick: false,
    didOpen: () => {
      const dropzoneElement = document.getElementById('file-dropzone');
      if (dropzoneElement && typeof Dropzone !== 'undefined') {
        Dropzone.autoDiscover = false;

        // Destruir instancia previa si existe
        if (dropzoneElement.dropzone) {
          dropzoneElement.dropzone.destroy();
        }

        dropzoneInstance = new Dropzone(dropzoneElement, {
          url: '#', // Dummy URL to prevent Dropzone uploads
          paramName: 'files',
          maxFilesize: 10, // 10MB
          maxFiles: 10,
          parallelUploads: 5,
          uploadMultiple: false, // Disable Dropzone’s upload
          addRemoveLinks: true,
          createImageThumbnails: true,
          thumbnailWidth: 50,
          thumbnailHeight: 50,
          timeout: 30000, // 30 segundos
          acceptedFiles: 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar',
          dictDefaultMessage: '<i class="bi bi-cloud-arrow-up fs-4 text-muted mb-1"></i><div class="small">Suelta archivos aquí o haz clic</div><div class="text-muted" style="font-size: 0.75rem;">Máximo 10MB por archivo - Hasta 10 archivos</div>',
          dictFallbackMessage: 'Tu navegador no soporta subida de archivos por drag & drop',
          dictFileTooBig: 'El archivo es demasiado grande ({{filesize}}MB). Tamaño máximo: {{maxFilesize}}MB.',
          dictInvalidFileType: 'No puedes subir archivos de este tipo',
          dictResponseError: 'Error al subir el archivo',
          dictCancelUpload: 'Cancelar subida',
          dictCancelUploadConfirmation: '¿Estás seguro de que quieres cancelar esta subida?',
          dictRemoveFile: 'Eliminar archivo',
          dictMaxFilesExceeded: 'Solo puedes subir hasta {{maxFiles}} archivos',
          autoProcessQueue: false, // Prevent automatic uploads
          init: function() {
            this.on('addedfile', function(file) {
              console.log('✅ Archivo añadido:', file.name, file.size, 'bytes');
              // Validate file type and size manually
              const acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain', 'application/zip', 'application/x-rar-compressed'];
              if (!acceptedTypes.includes(file.type) || file.size > 10 * 1024 * 1024) {
                this.removeFile(file);
                actionSwal({
                  icon: 'error',
                  title: 'Archivo no válido',
                  text: `El archivo ${file.name} no es del tipo permitido o excede el tamaño máximo de 10MB.`,
                  confirmButtonText: 'Aceptar'
                });
              }
            });

            this.on('removedfile', function(file) {
              console.log('🗑️ Archivo eliminado:', file.name);
            });

            this.on('canceled', function(file) {
              console.log('🚫 Subida cancelada:', file.name);
              // No mostrar error cuando se cancela intencionalmente
            });

            this.on('error', function(file, errorMessage, xhr) {
              // Ignorar errores de cancelación
              if (errorMessage && (errorMessage.includes('canceled') || errorMessage.includes('Canceled') || errorMessage === 'Upload canceled')) {
                console.log('🚫 Subida cancelada (ignorado):', file.name);
                return;
              }
              
              console.error('❌ Error en Dropzone:', file.name, errorMessage, xhr);
              if (file.accepted) {
                actionSwal({
                  icon: 'error',
                  title: 'Error',
                  text: `Error al procesar ${file.name}: ${errorMessage}`,
                  confirmButtonText: 'Aceptar'
                });
              }
            });
          }
        });

        console.log('Dropzone inicializado correctamente');
      } else {
        console.error('Dropzone no disponible');
        Swal.showValidationMessage('Error: Dropzone no se inicializó correctamente');
      }
    },
    preConfirm: () => {
      if (!dropzoneInstance) {
        Swal.showValidationMessage('Error: Dropzone no se inicializó correctamente');
        return false;
      }

      const queuedFiles = dropzoneInstance.getQueuedFiles();
      if (queuedFiles.length === 0) {
        Swal.showValidationMessage('Por favor, selecciona al menos un archivo para subir');
        return false;
      }

      console.log(`📁 Archivos listos para subir: ${queuedFiles.length}`);
      return queuedFiles;
    }
  });

  if (result.isConfirmed) {
    const progressBar = document.querySelector('.progress-bar');
    const progressContainer = document.getElementById('upload-progress');
    
    if (progressContainer) {
      progressContainer.style.display = 'block';
    } else {
      console.warn('Upload progress container not found');
    }

    try {
      const files = result.value;
      console.log(`🚀 Iniciando subida de ${files.length} archivos`);

      const formData = new FormData();

      // Agregar archivos
      files.forEach(file => {
        formData.append('files', file);
        console.log(`➕ Agregando archivo: ${file.name} (${file.size} bytes)`);
      });

      // Agregar parámetros requeridos
      formData.append('task_id', ticketId);
      formData.append('user_id', userDbId);

      console.log('📦 Datos del formulario:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      }

      if (progressBar) {
        progressBar.style.width = '50%';
      }

      const response = await fetch(getApiUrl('TASK_FILES'), {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      console.log('📨 Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `Error ${response.status}: ${response.statusText}` };
        }
        console.error('Error response:', errorData);
        throw new Error(errorData.error || `Error al subir archivos: ${response.status} ${response.statusText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Error parsing JSON:', e);
        throw new Error('Respuesta del servidor no válida');
      }

      if (progressBar) {
        progressBar.style.width = '100%';
      }

      console.log('✅ Respuesta exitosa:', data);

      // Update Dropzone file status to avoid error events
      files.forEach(file => {
        dropzoneInstance.emit('success', file, data);
        dropzoneInstance.removeFile(file); // Remove file from UI after success
      });

      if (refreshTickets) {
        void Promise.resolve(refreshTickets()).catch((err) => {
          console.warn('Error al refrescar tickets tras subir archivos:', err);
        });
      }
      actionSwal({
        icon: 'success',
        title: '¡Archivos subidos!',
        text: data.data?.msg || `Se subieron ${data.data?.total_uploaded || files.length} archivos correctamente.`,
        timer: 2800,
        showConfirmButton: false,
        timerProgressBar: true,
      });

    } catch (error) {
      console.error('💥 Error completo:', error);
      actionSwal({
        icon: 'error',
        title: 'Error al Subir Archivos',
        html: `
          <div class="text-start">
            <p><strong>No se pudieron subir los archivos:</strong></p>
            <p class="small">${escapeHtml(error.message)}</p>
            <p class="small mt-2">Verifica:</p>
            <ul class="small">
              <li>Que los archivos no excedan 10MB</li>
              <li>Que los tipos de archivo sean permitidos</li>
              <li>Tu conexión a internet</li>
            </ul>
          </div>
        `,
        confirmButtonText: 'Entendido'
      });
    } finally {
      if (progressContainer) {
        progressContainer.style.display = 'none';
      } else {
        console.warn('Upload progress container not found');
      }

      if (dropzoneInstance) {
        dropzoneInstance.destroy();
      }
    }
  } else {
    // Usuario canceló
    if (dropzoneInstance) {
      dropzoneInstance.destroy();
    }
  }
};


export const handleDeleteTicket = async (ticketId, refreshTickets, uiContext = null) => {
  const ref = actionTicketRefForSwal(ticketId, uiContext);
  actionSwalDanger({
    title: 'Eliminar Tarea',
    text: `¿Estás seguro de que quieres eliminar la tarea ${ref}? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
    preConfirm: async () =>
      runActionInPreConfirm(async () => {
        const response = await fetch(getApiUrl('TASK_BY_ID', ticketId), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Error deleting task: ${response.statusText}`);
        }
        return { msg: `La tarea ${ref} fue eliminada.` };
      }),
  }).then((result) => {
    if (result.isConfirmed) {
      fireActionSuccessWithRefresh('Eliminado', result.value?.msg, refreshTickets);
    }
  });
};

// Function for auto logout (sesión expirada / inactividad / token inválido)
export const performAutoLogout = (message = 'Sesión finalizada por inactividad') => {
  const pushPromise = notifyDesktopViaServiceWorkerOrFallback('Sesión finalizada', message, {
    tag: `hs-session-logout-${Date.now()}`,
    renotify: true,
    requireInteraction: false,
    silent: false,
    url: '/',
    inPageToastWhenVisible: false,
  }).catch(() => false);

  const safeMsg = escapeHtml(message);
  const swalRoot = document.getElementById('swal-root');
  const modalRoot = document.getElementById('modal-root');
  if (modalRoot) {
    modalRoot.dataset.swalBlocked = 'true';
  }

  Swal.fire({
    target: swalRoot || document.body,
    icon: 'info',
    title: 'Sesión finalizada',
    html: `<p class="mb-2">${safeMsg}</p><p class="text-muted small mb-0">Serás redirigido al inicio de sesión.</p>`,
    position: 'center',
    showConfirmButton: true,
    confirmButtonText: 'Aceptar',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showCloseButton: false,
    focusConfirm: true,
    heightAuto: false,
    scrollbarPadding: false,
    customClass: {
      container: 'swal-session-container',
      popup: 'swal-logout-modal',
      confirmButton: 'btn btn-primary',
    },
    backdrop: `rgba(0,0,0,0.45)`,
    didDestroy: () => {
      if (modalRoot) delete modalRoot.dataset.swalBlocked;
    },
  }).then(() => {
    if (modalRoot) delete modalRoot.dataset.swalBlocked;
    handleLogoutNoConfirm({ skipSwalRedirect: true });
  });

  return pushPromise.then((shown) => new Promise((resolve) => setTimeout(resolve, shown ? 2800 : 1000)));
};

// Function for logout without confirmation (cookie httpOnly: backend la borra)
const handleLogoutNoConfirm = async (opts = {}) => {
  const skipSwalRedirect = opts.skipSwalRedirect === true;
  try {
    await fetch(getApiUrl('LOGOUT'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
  } catch (error) {
    console.error('Error al cerrar sesión:', error);
  } finally {
    clearAuthData();
    if (skipSwalRedirect) {
      redirectToLogin();
    } else {
      Swal.fire({
        icon: 'success',
        title: 'Sesión finalizada',
        text: 'Serás redirigido al inicio de sesión.',
        timer: 2200,
        timerProgressBar: true,
        showConfirmButton: true,
        confirmButtonText: 'Ir ahora',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          container: 'swal2-logout-redirect',
          popup: 'swal-logout-modal',
          confirmButton: 'btn btn-primary',
        },
        zIndex: 100000,
      }).then(() => {
        redirectToLogin();
      });
    }
  }
};

// Function for manual logout with confirmation
export const handleLogout = async () => {
  const result = await Swal.fire({
    title: "Cerrar Sesión",
    text: "¿Estás seguro de que quieres cerrar sesión?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, cerrar",
    cancelButtonText: "Cancelar",
    customClass: {
      confirmButton: "btn btn-danger",
      cancelButton: "btn btn-outline-secondary",
      container: "swal2-logout-confirm",
    },
    zIndex: 99999,
  });

  if (result.isConfirmed) {
    await handleLogoutNoConfirm();
  }
};

// Function to handle password change - centralized (cookie httpOnly enviada con credentials)
export const handleChangePassword = async (userId, onCloseDropdown) => {
  if (!userId) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo identificar el usuario',
      confirmButtonText: 'Aceptar'
    });
    return;
  }

  // Mostrar loading
  Swal.fire({
    title: 'Generando token...',
    allowOutsideClick: false,
    width: '300px',
    padding: '1.5rem',
    customClass: {
      popup: 'swal2-popup-small',
      title: 'swal2-title-small'
    },
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    const url = getApiUrl('GENERATE_PASSWORD_RESET_TOKEN');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.status === 'success' && data.result?.token) {
      Swal.close();
      // Pasar el token en la URL pero será limpiado inmediatamente después de leerlo
      // Esto evita que quede en el historial del navegador
      const baseUrl = window.location.origin;
      // Codificar el token para asegurar que se pase correctamente en la URL
      const encodedToken = encodeURIComponent(data.result.token);
      // Abrir la página de cambio de contraseña con el token en la URL
      // El token será leído y la URL será limpiada inmediatamente
      const changePasswordUrl = `${baseUrl}/change-password#token=${encodedToken}`;
      
      // Log para depuración (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.log('Abriendo URL de cambio de contraseña:', changePasswordUrl.substring(0, 100) + '...');
      }
      
      window.open(changePasswordUrl, '_blank');
      
      // Cerrar el dropdown si se proporciona la función
      if (onCloseDropdown && typeof onCloseDropdown === 'function') {
        onCloseDropdown();
      }
    } else {
      throw new Error(data.result?.message || 'Error al generar el token');
    }
  } catch (error) {
    console.error('Error generando token de restablecimiento:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'Error al generar el token de restablecimiento de contraseña',
      confirmButtonText: 'Aceptar'
    });
  }
};